// Content URLs end in a human-readable title slug that different producers of the URL
// render differently, so only the part up to and including the content id can be
// compared. Returns null when the URL doesn't carry `contentId` as a path segment.
const getUrlUpToContentId = (url: string, contentId: string): string | null => {
	if (!contentId) {
		return null;
	}

	try {
		const { origin, pathname } = new URL(url);
		const segments = pathname.split('/');
		const contentIdIndex = segments.indexOf(contentId);

		return contentIdIndex === -1
			? null
			: `${origin}${segments.slice(0, contentIdIndex + 1).join('/')}`;
	} catch {
		return null;
	}
};

export const isSameContentUrl = (urlA: string, urlB: string, contentId: string): boolean => {
	const identityA = getUrlUpToContentId(urlA, contentId);

	return identityA !== null && identityA === getUrlUpToContentId(urlB, contentId);
};

// Matches the content id in the path of a Confluence content-view route. Mirrors the routes in
// `confluence/next/packages/named-routes/src/routes.ts`, which a test there keeps in sync: if it
// fails after a route change, add the new path segment to the alternatives below.
//
// The space key segment (`[^/]+`) covers every form: `_` (placeholder), `~ROVO-<uuid>~`, personal
// `~<accountId>` and normal keys. Longer alternatives come first so `pages/edit-v2` wins over
// `pages`.
const CONFLUENCE_CONTENT_ID_PATH_REGEX = new RegExp(
	'^/wiki/spaces/[^/]+/(?:pages/edit-v2|pages/edit-smart-create|pages/edit-embed|pages/edit|blog/edit-v2|blog/edit|blog/\\d+/\\d+/\\d+|next-pages|pages|blog|whiteboard|slide|custom/database|database|embed|folder)/([^/]+)',
	'i',
);

// Returns the content id of a Confluence content-view URL, or null for anything else (other
// schemes, unparsable URLs, and Confluence URLs that aren't a content view). Ignores the hostname,
// since custom domains and isolated cloud use their own, as well as the query string and fragment.
export const getConfluenceContentIdFromUrl = (url: string): string | null => {
	let parsed: URL;

	try {
		parsed = new URL(url);
	} catch {
		return null;
	}

	if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
		return null;
	}

	return CONFLUENCE_CONTENT_ID_PATH_REGEX.exec(parsed.pathname)?.[1] ?? null;
};
